/**
 * Sheet „Grundstück anlegen" (Spec 01, „Oberfläche", erweitert um Spec 02b,
 * „Schnellerfassung"). Sichtbar ist nur die Straße; alles andere steckt
 * hinter „Weitere Angaben", zugeklappt vorbelegt.
 *
 * Seit Spec 02b hat auch dieses Sheet „Anlegen und noch eins"
 * (`property-create-save-another`) und `property-duplicate` (alle Felder
 * außer der Straße vorbelegt). Der primäre Knopf bleibt synchron; „und noch
 * eins" speichert optimistisch (`useCreateProperty`s `optimistic`-Flag) —
 * dieselbe Regel wie in `BuildingSheet`/`UnitSheet`.
 */

import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { centsToDecimalInput, parseCents } from '@/lib/money'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import { useCreateProperty } from '@/features/properties/api'
import type { PropertyCreate, PropertyDetail } from '@/features/properties/api'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface PropertySheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Gesetzt für `property-duplicate` (Spec 02b, „Schnellerfassung ·
   * Duplizieren"): belegt alle Felder außer der Straße vor, die bleibt leer
   * und fokussiert. Ein Grundstück hat kein Elternobjekt. */
  readonly duplicateFrom?: PropertyDetail
}

const EMPTY_FORM = {
  street: '',
  postalCode: '',
  city: '',
  areaSqm: '',
  landRegistryRef: '',
  parcelNumber: '',
  acquiredOn: '',
  acquisitionCost: '',
}

type FormValues = typeof EMPTY_FORM

function deriveDuplicateForm(source: PropertyDetail): FormValues {
  return {
    street: '',
    postalCode: source.postal_code ?? '',
    city: source.city ?? '',
    areaSqm: source.area_sqm === null ? '' : areaToInputValue(source.area_sqm),
    landRegistryRef: source.land_registry_ref ?? '',
    parcelNumber: source.parcel_number ?? '',
    acquiredOn: source.acquired_on ?? '',
    acquisitionCost:
      source.acquisition_cost_cents === null ? '' : centsToDecimalInput(source.acquisition_cost_cents),
  }
}

function toBody(form: FormValues): PropertyCreate {
  return {
    street: form.street,
    postal_code: form.postalCode.trim() === '' ? null : form.postalCode.trim(),
    city: form.city.trim() === '' ? null : form.city.trim(),
    area_sqm: parseAreaInput(form.areaSqm),
    land_registry_ref: form.landRegistryRef.trim() === '' ? null : form.landRegistryRef.trim(),
    parcel_number: form.parcelNumber.trim() === '' ? null : form.parcelNumber.trim(),
    acquired_on: form.acquiredOn === '' ? null : form.acquiredOn,
    acquisition_cost_cents: parseCents(form.acquisitionCost),
  }
}

export function PropertySheet({ open, onOpenChange, duplicateFrom }: PropertySheetProps) {
  const navigate = useNavigate()
  const createMutation = useCreateProperty()
  const streetRef = useRef<HTMLInputElement>(null)

  // Lazy Initialisierer statt `EMPTY_FORM`: Seit die Detailquery (ADR 0028)
  // erst mit `isPropertyDuplicateSheetOpen` scharf geschaltet wird, mountet
  // dieses Sheet für „Duplizieren" **erstmals bereits mit `open === true`**
  // (`duplicateFrom` liegt vor, sonst würde `ObjectDetail` es gar nicht
  // rendern) — die untere „Jedes Öffnen beginnt frisch"-Umschaltung sieht
  // dann nie einen Wechsel von `false` auf `true` und säht das Formular nie.
  // Der Erststand übernimmt deshalb dieselbe Regel wie die Umschaltung.
  const [form, setForm] = useState<FormValues>(() =>
    open && duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM,
  )
  const [isMoreOpen, setIsMoreOpen] = useState(() => open && duplicateFrom !== undefined)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dieselbe Sheet-Instanz bleibt über mehrere
  // Öffnungen hinweg gemountet (der Baum hält sie dauerhaft), nur ihr Inhalt
  // wird von Radix aus- und wieder eingeblendet. Zurückgesetzt wird während
  // des Renderns (React-Muster „Adjusting state when a prop changes"), nicht
  // in einem Effekt — sonst gäbe es einen zusätzlichen Durchlauf, in dem das
  // alte Formular kurz sichtbar wäre. Beim Duplizieren vorbelegt, „Weitere
  // Angaben" gleich aufgeklappt (dieselbe Begründung wie in `BuildingSheet`).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM)
      setIsMoreOpen(duplicateFrom !== undefined)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    setForm(EMPTY_FORM)
    setIsMoreOpen(false)
    setFieldError(undefined)
    setGeneralError(undefined)
    streetRef.current?.focus()
  }

  async function submit(andAnother: boolean) {
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({ body: toBody(form), optimistic: andAnother })
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
        await navigate({
          to: '/objekt/$entityId',
          params: { entityId: created.id },
          search: { tab: undefined },
        })
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          streetRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Grundstück anlegen
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={streetRef}
              label="Straße und Hausnummer"
              testId="property-create-street"
              apiField="street"
              errorPrefix="property-create"
              required
              value={form.street}
              onChange={updateField('street')}
              error={fieldError}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="property-create-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <FormField
                  label="PLZ"
                  testId="property-create-postal-code"
                  apiField="postal_code"
                  errorPrefix="property-create"
                  inputMode="numeric"
                  value={form.postalCode}
                  onChange={updateField('postalCode')}
                  error={fieldError}
                />
                <FormField
                  label="Ort"
                  testId="property-create-city"
                  apiField="city"
                  errorPrefix="property-create"
                  value={form.city}
                  onChange={updateField('city')}
                  error={fieldError}
                />
                <FormField
                  label="Fläche (m²)"
                  testId="property-create-area-sqm"
                  apiField="area_sqm"
                  errorPrefix="property-create"
                  inputMode="decimal"
                  value={form.areaSqm}
                  onChange={updateField('areaSqm')}
                  error={fieldError}
                />
                <FormField
                  label="Grundbuchblatt"
                  testId="property-create-land-registry-ref"
                  apiField="land_registry_ref"
                  errorPrefix="property-create"
                  value={form.landRegistryRef}
                  onChange={updateField('landRegistryRef')}
                  error={fieldError}
                />
                <FormField
                  label="Flurstück"
                  testId="property-create-parcel-number"
                  apiField="parcel_number"
                  errorPrefix="property-create"
                  value={form.parcelNumber}
                  onChange={updateField('parcelNumber')}
                  error={fieldError}
                />
                <FormField
                  label="Anschaffungsdatum"
                  testId="property-create-acquired-on"
                  apiField="acquired_on"
                  errorPrefix="property-create"
                  type="date"
                  value={form.acquiredOn}
                  onChange={updateField('acquiredOn')}
                  error={fieldError}
                />
                <FormField
                  label="Anschaffungskosten"
                  testId="property-create-acquisition-cost"
                  apiField="acquisition_cost_cents"
                  errorPrefix="property-create"
                  inputMode="decimal"
                  value={form.acquisitionCost}
                  onChange={updateField('acquisitionCost')}
                  error={fieldError}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="property-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="property-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="property-create-save-another"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                onClick={() => void submit(true)}
                className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
              >
                Anlegen und noch eins
              </button>
              <button
                type="submit"
                data-testid="property-create-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Grundstück anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
